/*
 * Copyright (c) 2023 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import {
    getDelayedDownloadingsSuccessAction,
    getDelayedDownloadingsErrorAction,
    DownloadingsAction,
    DownloadingsActionType,
    deleteDelayedDownloadingsItemSuccessAction,
    deleteDelayedDownloadingsItemErrorAction,
    downloadFileFromTheListSuccessAction,
    downloadFileFromTheListErrorAction,
    pollingDelayedDownloadingsSuccessAction,
    pollingDelayedDownloadingsErrorAction,
} from '../actions/downloadingsActions';
import { switchMap, map, catchError, takeUntil } from 'rxjs/operators';
import { of, from, interval } from 'rxjs';
import { AxiosError } from 'axios';
import { NOTIFICATION_TYPES, downloadFileByUrl, parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';
import { downloadingsClient } from '../api/downloadings/downloadingsClient';
import { StoresActionType, StoresActions } from '../actions/storesActions';
import { CompaniesAction, CompaniesActionType } from '../actions/companiesActions';
import { CashBoxesAction, CashBoxesActionType } from '../actions/CashBoxRegistry/cashBoxesActions';
import { OperatorsActionType, OperatorsActions } from '../actions/operatorsActions';
import { DocumentsAction, DocumentsActionType } from '../actions/documentsActions';
import { DevicesAction, DevicesActionType } from '../actions/devicesActions';
import {
    CashBoxDocumentsTabAction,
    CashBoxDocumentsTabActionType,
} from '../actions/CashBoxRegistry/Tabs/cashBoxDocumentsTabActions';
import { UserActionType } from '../actions/userActions';
import { ReportsActionType } from '../actions/reportsActions';

type DownloadingsEpic = Epic<
    | DownloadingsAction
    | OperatorsActions
    | StoresActions
    | CompaniesAction
    | CashBoxesAction
    | CashBoxDocumentsTabAction
    | DocumentsAction
    | DevicesAction,
    DownloadingsAction
>;

export const getDelayedDownloadingsListEpic: DownloadingsEpic = (action$) => {
    return action$.pipe(
        ofType(
            DownloadingsActionType.GET_DELAYED_DOWNLOADINGS_START_ACTION,
            DownloadingsActionType.DELETE_DELAYED_DOWNLOADINGS_ITEMS_SUCCESS_ACTION,
            OperatorsActionType.DOWNLOAD_EXCEL_SUCCESS,
            StoresActionType.DOWNLOAD_EXCEL_SUCCESS,
            CompaniesActionType.DOWNLOAD_EXCEL_SUCCESS,
            CashBoxesActionType.DOWNLOAD_EXCEL_SUCCESS,
            CashBoxDocumentsTabActionType.DOWNLOAD_DOCUMENTS_SUCCESS,
            DocumentsActionType.DOWNLOAD_EXCEL_SUCCESS,
            DevicesActionType.DOWNLOAD_EXCEL_SUCCESS,
            ReportsActionType.DOWNLOAD_REPORT_SUCCESS,
        ),
        switchMap(() => {
            return from(downloadingsClient.getDelayedDownloadings()).pipe(
                map((list) => {
                    return getDelayedDownloadingsSuccessAction(list);
                }),

                catchError((err: AxiosError) =>
                    of(
                        getDelayedDownloadingsErrorAction(
                            parseError(err, i18n.t('downloadings.errors.failedToGetDelayedDownloadings')),
                        ),
                    ),
                ),
            );
        }),
    );
};

const UPDATE_DOWNLOADINGS_LIST_POLLING_INTERVAL = 20000;

export const delayedDownloadingsPollingEpic: DownloadingsEpic = (action$) => {
    return action$.pipe(
        ofType(DownloadingsActionType.START_DELAYED_DOWNLOADINGS_POLLING_ACTION),
        switchMap(() => {
            return interval(UPDATE_DOWNLOADINGS_LIST_POLLING_INTERVAL).pipe(
                switchMap(() => {
                    return from(downloadingsClient.getDelayedDownloadings()).pipe(
                        map((list) => {
                            return pollingDelayedDownloadingsSuccessAction(list);
                        }),

                        catchError((err: AxiosError) =>
                            of(
                                pollingDelayedDownloadingsErrorAction(
                                    parseError(err, i18n.t('downloadings.errors.failedToPollDelayedDownloadings')),
                                ),
                            ),
                        ),
                    );
                }),
                takeUntil(
                    action$.pipe(
                        ofType(
                            DownloadingsActionType.STOP_DELAYED_DOWNLOADINGS_POLLING_ACTION,
                            UserActionType.LOGOUT_USER,
                        ),
                    ),
                ),
            );
        }),
    );
};

export const deleteDelayedDownloadingsListItemEpic: Epic<DownloadingsAction> = (action$) => {
    return action$.pipe(
        ofType(DownloadingsActionType.DELETE_DELAYED_DOWNLOADINGS_ITEMS_START_ACTION),
        switchMap((action) => {
            return from(downloadingsClient.deleteDelayedDownloadingsItem(action.payload)).pipe(
                map(() => {
                    return deleteDelayedDownloadingsItemSuccessAction({
                        type: NOTIFICATION_TYPES[1],
                        message: i18n.t('app.filesDeleted'),
                    });
                }),
                catchError((err: AxiosError) =>
                    of(deleteDelayedDownloadingsItemErrorAction(parseError(err, i18n.t('errors.failedToDeleteFiles')))),
                ),
            );
        }),
    );
};

export const downloadFileFromDelayedDownloadingsListEpic: Epic<DownloadingsAction> = (action$) => {
    return action$.pipe(
        ofType(DownloadingsActionType.DOWNLOAD_FILE_FROM_THE_LIST_START_ACTION),
        switchMap((action) => {
            const fileName = action.payload.fileNameForUser;
            return from(downloadingsClient.getUrlForDownloading(action.payload.fileNameS3)).pipe(
                switchMap((url) => {
                    return from(downloadFileByUrl(fileName, url)).pipe(
                        map(() => downloadFileFromTheListSuccessAction()),

                        catchError((err: AxiosError) =>
                            of(
                                downloadFileFromTheListErrorAction(
                                    parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                                ),
                            ),
                        ),
                    );
                }),
                catchError((err: AxiosError) =>
                    of(
                        downloadFileFromTheListErrorAction(
                            parseError(err, i18n.t('errors.failedToGetUrl', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};
